import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, MessageSquare, RefreshCw, Sparkles, TrendingUp, TrendingDown, Minus, AlertTriangle, Target, Activity } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Progress } from "@/components/ui/progress";

interface ConversationAnalysis {
  id: string;
  conversation_id: string;
  employee_id: string;
  overall_mood_score: number;
  mood_label: string;
  trend_direction: number;
  key_topics: unknown;
  risks: unknown;
  summary: string;
  analyzed_at: string;
}

interface MoodStatistics {
  month: string;
  total_conversations: number;
  avg_mood_score: number;
  kritisch_count: number;
  angespannt_count: number;
  stabil_count: number;
  positiv_count: number;
  sehr_positiv_count: number;
  avg_trend: number;
}

interface PendingConversation {
  id: string;
  conversation_date: string;
  conversation_type: string;
}

const moodLabelColors: Record<string, string> = {
  kritisch: "bg-red-500/20 text-red-400 border-red-500/30",
  angespannt: "bg-orange-500/20 text-orange-400 border-orange-500/30",
  stabil: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  positiv: "bg-green-500/20 text-green-400 border-green-500/30",
  sehr_positiv: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
};

const moodLabelNames: Record<string, string> = {
  kritisch: "Kritisch",
  angespannt: "Angespannt",
  stabil: "Stabil",
  positiv: "Positiv",
  sehr_positiv: "Sehr Positiv",
};

export function ConversationAISummary() {
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [analyses, setAnalyses] = useState<ConversationAnalysis[]>([]);
  const [pendingConversations, setPendingConversations] = useState<PendingConversation[]>([]);
  const [aggregatedData, setAggregatedData] = useState<{
    avgMoodScore: number;
    avgTrend: number;
    totalAnalyzed: number;
    moodDistribution: Record<string, number>;
    allTopics: Record<string, number>;
    allRisks: Record<string, number>;
  } | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load existing analyses
      const { data: analysesData, error: analysesError } = await supabase
        .from("conversation_analysis")
        .select("*")
        .order("analyzed_at", { ascending: false });

      // Cast the data to our interface
      const typedData = (analysesData || []) as unknown as ConversationAnalysis[];
      
      if (analysesError) {
        console.error("Error loading analyses:", analysesError);
      } else {
        setAnalyses(typedData);
        
        // Aggregate data from analyses
        if (typedData && typedData.length > 0) {
          const totalAnalyzed = typedData.length;
          const avgMoodScore = typedData.reduce((sum, a) => sum + a.overall_mood_score, 0) / totalAnalyzed;
          const avgTrend = typedData.reduce((sum, a) => sum + a.trend_direction, 0) / totalAnalyzed;
          
          const moodDistribution: Record<string, number> = {};
          const allTopics: Record<string, number> = {};
          const allRisks: Record<string, number> = {};
          
          typedData.forEach(a => {
            moodDistribution[a.mood_label] = (moodDistribution[a.mood_label] || 0) + 1;
            
            const topics = Array.isArray(a.key_topics) ? a.key_topics : [];
            topics.forEach((topic: string) => {
              allTopics[topic] = (allTopics[topic] || 0) + 1;
            });
            
            const risks = Array.isArray(a.risks) ? a.risks : [];
            risks.forEach((risk: string) => {
              allRisks[risk] = (allRisks[risk] || 0) + 1;
            });
          });
          
          setAggregatedData({
            avgMoodScore: Math.round(avgMoodScore * 10) / 10,
            avgTrend: Math.round(avgTrend * 100) / 100,
            totalAnalyzed,
            moodDistribution,
            allTopics,
            allRisks,
          });
        }
      }

      // Find conversations that haven't been analyzed yet
      const { data: completedConversations } = await supabase
        .from("employee_conversations")
        .select("id, conversation_date, conversation_type")
        .eq("status", "abgeschlossen")
        .order("conversation_date", { ascending: false });

      if (completedConversations) {
        const analyzedIds = new Set(typedData.map(a => a.conversation_id));
        const pending = completedConversations.filter(c => !analyzedIds.has(c.id));
        setPendingConversations(pending);
      }

    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setLoading(false);
    }
  };

  const analyzeAllPending = async () => {
    if (pendingConversations.length === 0) {
      toast.info("Alle Gespräche wurden bereits analysiert");
      return;
    }

    setAnalyzing(true);
    let successCount = 0;
    let errorCount = 0;

    for (const conversation of pendingConversations) {
      try {
        const { error } = await supabase.functions.invoke("analyze-conversation", {
          body: { conversationId: conversation.id }
        });

        if (error) {
          console.error(`Error analyzing ${conversation.id}:`, error);
          errorCount++;
        } else {
          successCount++;
        }
      } catch (err) {
        console.error(`Error analyzing ${conversation.id}:`, err);
        errorCount++;
      }
    }

    setAnalyzing(false);
    
    if (successCount > 0) {
      toast.success(`${successCount} Gespräch(e) erfolgreich analysiert`);
    }
    if (errorCount > 0) {
      toast.error(`${errorCount} Analyse(n) fehlgeschlagen`);
    }

    // Reload data
    await loadData();
  };

  const getTrendIcon = (trend: number) => {
    if (trend > 0.1) return <TrendingUp className="h-4 w-4 text-green-500" />;
    if (trend < -0.1) return <TrendingDown className="h-4 w-4 text-red-500" />;
    return <Minus className="h-4 w-4 text-muted-foreground" />;
  };

  const getMoodScoreColor = (score: number) => {
    if (score >= 71) return "text-green-500";
    if (score >= 51) return "text-blue-500";
    if (score >= 31) return "text-orange-500";
    return "text-red-500";
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="col-span-full">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            KI-Stimmungsanalyse Mitarbeitergespräche
          </CardTitle>
          <p className="text-sm text-muted-foreground mt-1">
            {aggregatedData?.totalAnalyzed || 0} analysierte Gespräche
            {pendingConversations.length > 0 && (
              <span className="ml-2 text-amber-500">
                • {pendingConversations.length} ausstehend
              </span>
            )}
          </p>
        </div>
        <Button 
          onClick={analyzeAllPending} 
          disabled={analyzing || pendingConversations.length === 0}
          size="sm"
        >
          {analyzing ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Analysiere...
            </>
          ) : (
            <>
              <RefreshCw className="h-4 w-4 mr-2" />
              {pendingConversations.length > 0 
                ? `${pendingConversations.length} analysieren` 
                : "Alle analysiert"}
            </>
          )}
        </Button>
      </CardHeader>
      <CardContent>
        {(!aggregatedData || aggregatedData.totalAnalyzed === 0) ? (
          <div className="text-center py-8 text-muted-foreground">
            <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>Keine analysierten Mitarbeitergespräche vorhanden</p>
            {pendingConversations.length > 0 && (
              <p className="text-sm mt-2">
                Klicken Sie auf "Analysieren" um {pendingConversations.length} Gespräch(e) zu analysieren.
              </p>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Overall Mood Score */}
            <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold flex items-center gap-2">
                  <Activity className="h-4 w-4" />
                  Durchschnittliche Stimmung
                </h4>
                {getTrendIcon(aggregatedData.avgTrend)}
              </div>
              <div className="flex items-end gap-2 mb-2">
                <span className={`text-4xl font-bold ${getMoodScoreColor(aggregatedData.avgMoodScore)}`}>
                  {aggregatedData.avgMoodScore}
                </span>
                <span className="text-muted-foreground mb-1">/ 100</span>
              </div>
              <Progress 
                value={aggregatedData.avgMoodScore} 
                className="h-2 mb-2"
              />
              <p className="text-xs text-muted-foreground">
                Trend: {aggregatedData.avgTrend > 0 ? "+" : ""}{aggregatedData.avgTrend}
              </p>
            </div>

            {/* Mood Distribution */}
            <div className="p-4 bg-muted/30 rounded-lg border">
              <h4 className="font-semibold mb-3">Stimmungsverteilung</h4>
              <div className="space-y-2">
                {Object.entries(moodLabelNames).map(([key, name]) => {
                  const count = aggregatedData.moodDistribution[key] || 0;
                  const percentage = aggregatedData.totalAnalyzed > 0 
                    ? Math.round((count / aggregatedData.totalAnalyzed) * 100) 
                    : 0;
                  return (
                    <div key={key} className="flex items-center gap-2">
                      <Badge variant="outline" className={`${moodLabelColors[key]} text-xs`}>
                        {name}
                      </Badge>
                      <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                        <div 
                          className={`h-full ${key === 'kritisch' ? 'bg-red-500' : 
                            key === 'angespannt' ? 'bg-orange-500' : 
                            key === 'stabil' ? 'bg-blue-500' : 
                            key === 'positiv' ? 'bg-green-500' : 'bg-emerald-500'}`}
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                      <span className="text-xs text-muted-foreground w-8">{count}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Key Topics */}
            <div className="p-4 bg-muted/30 rounded-lg border">
              <h4 className="font-semibold flex items-center gap-2 mb-3">
                <Target className="h-4 w-4" />
                Häufigste Themen
              </h4>
              <div className="flex flex-wrap gap-2">
                {Object.entries(aggregatedData.allTopics)
                  .sort((a, b) => b[1] - a[1])
                  .slice(0, 8)
                  .map(([topic, count]) => (
                    <Badge key={topic} variant="secondary" className="text-xs">
                      {topic} ({count})
                    </Badge>
                  ))}
                {Object.keys(aggregatedData.allTopics).length === 0 && (
                  <p className="text-sm text-muted-foreground">Keine Themen erkannt</p>
                )}
              </div>
            </div>

            {/* Risks */}
            {Object.keys(aggregatedData.allRisks).length > 0 && (
              <div className="md:col-span-2 lg:col-span-3 p-4 bg-red-500/5 rounded-lg border border-red-500/20">
                <h4 className="font-semibold flex items-center gap-2 mb-3 text-red-400">
                  <AlertTriangle className="h-4 w-4" />
                  Erkannte Risiken
                </h4>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(aggregatedData.allRisks)
                    .sort((a, b) => b[1] - a[1])
                    .map(([risk, count]) => (
                      <Badge 
                        key={risk} 
                        variant="outline" 
                        className="bg-red-500/10 text-red-400 border-red-500/30"
                      >
                        {risk} ({count}x)
                      </Badge>
                    ))}
                </div>
              </div>
            )}

            {/* Recent Summaries */}
            {analyses.length > 0 && (
              <div className="md:col-span-2 lg:col-span-3 space-y-3">
                <h4 className="font-semibold flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-primary" />
                  Letzte Analysen
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {analyses.slice(0, 4).map((analysis) => (
                    <div 
                      key={analysis.id} 
                      className="p-3 bg-muted/30 rounded-lg border text-sm"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <Badge 
                          variant="outline" 
                          className={moodLabelColors[analysis.mood_label]}
                        >
                          {moodLabelNames[analysis.mood_label] || analysis.mood_label}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          {new Date(analysis.analyzed_at).toLocaleDateString("de-DE")}
                        </span>
                      </div>
                      <p className="text-muted-foreground line-clamp-2">
                        {analysis.summary || "Keine Zusammenfassung verfügbar"}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
